import { format, parseISO } from 'date-fns';
import type { Locale as DateFnsLocale } from 'date-fns';
import { RestaurantWeekMenus } from '@durma-soft/gros-sdk/dist/types/restaurants';

export interface WeekMenuPrintMenu {
  id: number;
  name: string;
  foods: string[];
}

export interface WeekMenuPrintShift {
  shift: string;
  label?: string;
  menus: WeekMenuPrintMenu[];
}

export interface WeekMenuPrintDay {
  date: string;
  weekday: string;
  dateLabel: string;
  shifts: WeekMenuPrintShift[];
}

export interface WeekMenuPrintShiftInfo {
  shift: string;
  label?: string;
}

export interface WeekMenuPrintData {
  days: WeekMenuPrintDay[];
  shifts: WeekMenuPrintShiftInfo[];
}

interface BuildWeekMenuPrintDataOptions {
  locale: DateFnsLocale;
  shiftWord: string;
}

const parseDay = (date: string) => parseISO(date);

const capitalize = (value: string) =>
  value.charAt(0).toUpperCase() + value.slice(1);

export const buildWeekMenuPrintData = (
  data: RestaurantWeekMenus,
  { locale, shiftWord }: BuildWeekMenuPrintDataOptions,
): WeekMenuPrintData => {
  const usedShifts = new Set<string>();

  const days = Object.entries(data)
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([date, shifts]) => {
      const printShifts = Object.entries(shifts)
        .sort(([a], [b]) => Number(a) - Number(b))
        .map(([shift, menus]) => ({
          shift,
          menus: menus
            .filter((menu) => menu.is_active)
            .map((menu) => ({
              id: menu.id,
              name: menu.menu_type.name,
              foods: menu.foods.map((food) => food.name),
            })),
        }))
        .filter((shift) => shift.menus.length > 0);

      printShifts.forEach((shift) => usedShifts.add(shift.shift));

      const day = parseDay(date);
      return {
        date,
        weekday: capitalize(format(day, 'EEEE', { locale })),
        dateLabel: format(day, 'dd.MM.'),
        shifts: printShifts,
      };
    })
    .filter((day) => day.shifts.length > 0);

  const showShiftLabels = usedShifts.size > 1;
  const labelFor = (shift: string) =>
    showShiftLabels ? `${shift}. ${shiftWord.toLowerCase()}` : undefined;

  return {
    days: days.map((day) => ({
      ...day,
      shifts: day.shifts.map((shift) => ({
        ...shift,
        label: labelFor(shift.shift),
      })),
    })),
    shifts: [...usedShifts]
      .sort((a, b) => Number(a) - Number(b))
      .map((shift) => ({ shift, label: labelFor(shift) })),
  };
};

export const formatWeekRangeLabel = (weekStart: Date, weekEnd: Date) =>
  `${format(weekStart, 'dd.MM.')} – ${format(weekEnd, 'dd.MM.yyyy.')}`;
